import { useMemo, useState } from 'react';

// Ordenación client-side reutilizable para las tablas del backoffice.
//
// `valueFn(item, key)` devuelve el valor comparable de esa columna:
//   - fechas → ms epoch (number)
//   - texto  → minúsculas (orden case-insensitive)
//   - booleanos → 1/0
//   - ausente → null (los null van SIEMPRE al final, en ambas direcciones)
//
// Uso:
//   const { sorted, toggle, arrow } = useSort(rows, valueFn);
//   <th className="sortable" onClick={() => toggle('email')}>Email{arrow('email')}</th>

export type SortDir = 'asc' | 'desc';
export type SortValue = number | string | null;

export function useSort<T, K extends string>(
  items: T[],
  valueFn: (item: T, key: K) => SortValue,
  initial: { key: K; dir: SortDir } | null = null,
) {
  const [sort, setSort] = useState<{ key: K; dir: SortDir } | null>(initial);

  // Primer clic en una columna → asc; siguientes alternan asc/desc.
  function toggle(key: K) {
    setSort((prev) =>
      prev && prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  }

  function arrow(key: K): string {
    if (!sort || sort.key !== key) return '';
    return sort.dir === 'asc' ? ' ▲' : ' ▼';
  }

  const sorted = useMemo(() => {
    if (!sort) return items;
    const { key, dir } = sort;
    const factor = dir === 'asc' ? 1 : -1;

    // Copia antes de ordenar (no mutar el array de entrada).
    return [...items].sort((a, b) => {
      const va = valueFn(a, key);
      const vb = valueFn(b, key);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return 0;
    });
    // valueFn es lógica estable definida inline en cada consumidor; solo
    // reordenamos cuando cambian los datos o el criterio.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, sort]);

  return { sorted, toggle, arrow, sort };
}
